/**
 * Formats a date string or Date object to a readable date format
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Date';
    
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid Date';
  }
};

/**
 * Formats a date string or Date object to a readable time format
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted time string
 */
export const formatTime = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid Time';
    
    return dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid Time';
  }
};

/**
 * Formats a date string or Date object to include both date and time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date) => {
  if (!date) return 'N/A';
  
  const formattedDate = formatDate(date);
  const formattedTime = formatTime(date);
  
  if (formattedDate === 'Invalid Date' || formattedTime === 'Invalid Time') {
    return 'Invalid Date/Time';
  }
  
  return `${formattedDate} at ${formattedTime}`;
};

/**
 * Checks if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPastDate = (date) => {
  if (!date) return false;
  
  try {
    const dateObj = new Date(date);
    const now = new Date();
    return dateObj < now;
  } catch (error) {
    console.error('Error checking if date is past:', error);
    return false;
  }
};

/**
 * Gets the relative time from now (e.g., "2 hours ago", "in 3 days")
 * @param {string|Date} date - The date to compare
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return 'N/A';
  
  try {
    const dateObj = new Date(date);
    const now = new Date();
    const diffInSeconds = Math.floor((dateObj - now) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (Math.abs(diffInSeconds) < 60) {
      return diffInSeconds < 0 ? 'Just now' : 'In a moment';
    } else if (Math.abs(diffInMinutes) < 60) {
      return diffInMinutes < 0 
        ? `${Math.abs(diffInMinutes)} minute${Math.abs(diffInMinutes) !== 1 ? 's' : ''} ago`
        : `In ${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''}`;
    } else if (Math.abs(diffInHours) < 24) {
      return diffInHours < 0 
        ? `${Math.abs(diffInHours)} hour${Math.abs(diffInHours) !== 1 ? 's' : ''} ago`
        : `In ${diffInHours} hour${diffInHours !== 1 ? 's' : ''}`;
    } else {
      return diffInDays < 0 
        ? `${Math.abs(diffInDays)} day${Math.abs(diffInDays) !== 1 ? 's' : ''} ago`
        : `In ${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
    }
  } catch (error) {
    console.error('Error getting relative time:', error);
    return 'N/A';
  }
};